import accounting from "accounting"
import dayjs from "dayjs"

import {
  CASE_NETWORK_META,
  CASE_REASON,
  CASE_VERDICT,
  EVIDENCE_LABEL,
  TIMELINE_EVENT,
  VERDICT_TEXT
} from "../../constants/case-details"

const { formatNumber } = accounting

// The open case lives in the URL, so a case can be linked to and Back closes the drawer.
export const CASE_DRAWER_PARAM = "case_id"

export const formatMoney = (value) => "$" + formatNumber(value ?? 0, 2, ",")

const formatUtc = (at) => `${dayjs.utc(at).format("DD MMM YYYY h:mm A")} (UTC)`

const withoutFinalDot = (text) => text.replace(/\.$/, "")

const networkOf = (program) => CASE_NETWORK_META[program]?.network

export const verdictText = ({ program, verdict, reason }, t) => {
  const network = networkOf(program)
  if (verdict === CASE_VERDICT.AWAITING) return t("Waiting for _network_'s decision.", { network })
  if (reason === CASE_REASON.NO_DEVICE) {
    return t("No IP or device was sent, so _network_ couldn't compare past orders.", { network })
  }
  if (reason === CASE_REASON.NO_MATCH && verdict === CASE_VERDICT.NOT_BLOCKED) {
    return t("Visa didn't find 2 matching past orders.")
  }
  return VERDICT_TEXT[verdict] ? t(VERDICT_TEXT[verdict]) : ""
}

export const pastOrdersEmptyText = ({ program, verdict, reason }, t) => {
  if (reason === CASE_REASON.NO_DEVICE) {
    return t("Not compared: _network_ needs an IP or device on the disputed order.", {
      network: networkOf(program)
    })
  }
  if (verdict === CASE_VERDICT.NOT_ASSESSED)
    return t("Mastercard found no matching order to start from.")
  return t(CASE_NETWORK_META[program]?.noPastOrders ?? "")
}

export const pastOrderMatchText = (order, { verdict }, t) => {
  if (verdict === CASE_VERDICT.TIMEOUT) return t("Sent after the time limit")
  if (!order.matched_fields?.length) return t("No matching details")
  return t("Matches: _fields_", {
    fields: order.matched_fields.map((field) => t(EVIDENCE_LABEL[field])).join(", ")
  })
}

const pastOrdersRequestedText = ({ count }, detail, t) => {
  const network = networkOf(detail.program)
  if (count === 1) return t("_network_ requested 1 past order", { network })
  return t("_network_ requested _count_ past orders", { network, count })
}

const TIMELINE_TITLE = {
  [TIMELINE_EVENT.DISPUTE_RECEIVED]: (_, detail, t) =>
    t("Fraud dispute received · reason code _code_", { code: detail.reason_code }),
  [TIMELINE_EVENT.PAST_ORDERS_REQUESTED]: pastOrdersRequestedText,
  [TIMELINE_EVENT.NO_PAST_ORDERS]: (_, detail, t) =>
    t(CASE_NETWORK_META[detail.program]?.noPastOrdersEvent ?? ""),
  [TIMELINE_EVENT.PAST_ORDERS_NOT_REQUESTED]: (_, __, t) => t("Past orders not requested"),
  [TIMELINE_EVENT.DISPUTED_ORDER_CHECKED]: (_, __, t) => t("Mastercard checked the disputed order"),
  // The decision event carries its own verdict: on a reversed case it is the block that came first.
  [TIMELINE_EVENT.DECISION]: (item, detail, t) =>
    withoutFinalDot(verdictText({ ...detail, verdict: item.verdict }, t)),
  [TIMELINE_EVENT.BLOCK_REVERSED]: (_, __, t) =>
    withoutFinalDot(t(VERDICT_TEXT[CASE_VERDICT.REVERSED])),
  [TIMELINE_EVENT.AWAITING_DECISION]: (_, detail, t) =>
    t("Waiting for _network_'s decision", { network: networkOf(detail.program) })
}

export const timelineTitle = (item, detail, t) =>
  TIMELINE_TITLE[item.event]?.(item, detail, t) ?? ""

// `fee` is absent on a step that charges nothing by itself, and 0 on a final step that didn't charge.
const feeText = (fee, t) => {
  if (fee == null) return null
  if (fee > 0) return t("Fee _amount_", { amount: formatMoney(fee) })
  return t("No fee")
}

export const timelineSubtitle = (item, t) => {
  if (item.event === TIMELINE_EVENT.AWAITING_DECISION) {
    return t("Since _at_ · Decisions can take a few days", { at: formatUtc(item.at) })
  }
  return [formatUtc(item.at), t("by System"), feeText(item.fee, t)].filter(Boolean).join(" · ")
}

const STOPPING_VERDICTS = [CASE_VERDICT.BLOCKED, CASE_VERDICT.SHIFTED]

export const timelineColor = (item) => {
  if (item.event === TIMELINE_EVENT.AWAITING_DECISION) return "purple"
  if (item.event === TIMELINE_EVENT.DECISION && STOPPING_VERDICTS.includes(item.verdict)) {
    return "green"
  }
  return "gray"
}
